'use client'

import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react'

import { resolveFeatureFlags } from '@/lib/feature-flags'

interface FeatureFlagContextType {
  flags: Record<string, boolean>
  can: (key: string) => boolean
}

const FeatureFlagContext = createContext<FeatureFlagContextType | undefined>(undefined)

interface FeatureFlagProviderProps {
  children: ReactNode
  hostname: string
}

export function FeatureFlagProvider({ children, hostname }: FeatureFlagProviderProps) {
  const flags = useMemo(() => resolveFeatureFlags(hostname), [hostname])
  const can = useCallback((key: string) => flags[key] ?? false, [flags])

  return <FeatureFlagContext.Provider value={{ flags, can }}>{children}</FeatureFlagContext.Provider>
}

export function useFeatureFlags(): FeatureFlagContextType {
  const ctx = useContext(FeatureFlagContext)
  if (!ctx) throw new Error('useFeatureFlags must be used within FeatureFlagProvider')
  return ctx
}

export function useCan(key: string): boolean {
  const { can } = useFeatureFlags()
  return can(key)
}
